import jsPDF from 'jspdf';
import logoWhiteUrl from '../assets/LOGO WHITE.svg';

const C = {
  blue: [0, 74, 173],
  dark: [21, 39, 68],
  navy: [17, 34, 58],
  ink: [15, 23, 42],
  s800: [30, 41, 59],
  s700: [51, 65, 85],
  s600: [71, 85, 105],
  s500: [100, 116, 139],
  s400: [148, 163, 184],
  s300: [203, 213, 225],
  s200: [226, 232, 240],
  s100: [241, 245, 249],
  s50: [248, 250, 252],
  white: [255, 255, 255],
  rose: [244, 63, 94],
  orange: [249, 115, 22],
  amber: [245, 158, 11],
  emerald: [16, 185, 129],
  sky: [14, 165, 233],
  indigo: [99, 102, 241],
  teal: [20, 184, 166],
};

const W = 210;
const H = 297;
const ML = 14;
const CW = W - ML * 2;
const HDR_H = 17;
const BODY_Y = HDR_H + 9;
const FOOTER_Y = H - 12;
const DIM_COLORS = [C.blue, C.sky, C.indigo, C.teal, C.amber];

const DIM_LABEL = {
  'Disponibilidad Logística': 'Disponibilidad',
  Costos: 'Costos',
  'Comunicación y Cumplimiento Naviero': 'Cumplimiento',
  'Operación Portuaria': 'Operación',
  'Experiencia General': 'Experiencia',
};

const clamp = (value, min = 0, max = 100) => Math.min(max, Math.max(min, Number(value) || 0));
const number = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const oneDecimal = (value) => number(value).toFixed(1);
const compactNumber = (value) => {
  const parsed = number(value);
  return Number.isInteger(parsed) ? parsed.toString() : parsed.toFixed(1);
};

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 212;
      canvas.height = 95;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = logoWhiteUrl;
  });

export const generateExecutivePDF = async (data = {}) => {
  const {
    period = '—',
    globalIndex = 0,
    interpretation = { text: '' },
    participantCount = 0,
    dimensionDrivers = [],
    dimensionDeltas = {},
    summaryInsights = { critical: [], alerts: [] },
    sentimentDisplay = null,
    sectorLeaderboard = [],
    destinationLeaderboard = [],
    portLeaderboard = [],
    featuredVerbatims = [],
    globalDeltaMeta = null,
    laggingDimension = null,
    trendStats = [],
    executiveSummary = '',
    navieraStats = [],
    segmentPressure = { sectors: [], destinations: [], ports: [] },
    operationalFriction = null,
    aiNarrative = null,
  } = data;

  const logoDataUrl = await loadLogo();
  const pdf = new jsPDF('p', 'mm', 'a4');
  const totalPages = aiNarrative ? 4 : 3;
  let page = 1;

  const fill = (color) => pdf.setFillColor(...color);
  const stroke = (color) => pdf.setDrawColor(...color);
  const txt = (color) => pdf.setTextColor(...color);
  const fnt = (size, style = 'normal') => {
    pdf.setFont('helvetica', style);
    pdf.setFontSize(size);
  };

  const wrap = (value, maxWidth, maxLines = 1) => {
    const lines = pdf.splitTextToSize(String(value || ''), maxWidth);
    return lines.slice(0, maxLines);
  };

  const line = (x, y, w, color = C.s200, width = 0.2) => {
    stroke(color);
    pdf.setLineWidth(width);
    pdf.line(x, y, x + w, y);
  };

  const rect = (x, y, w, h, bg = C.white, border = C.s200, radius = 1.8) => {
    fill(bg);
    stroke(border);
    pdf.setLineWidth(0.22);
    pdf.roundedRect(x, y, w, h, radius, radius, 'FD');
  };

  const bar = (x, y, w, h, pct, color, bg = C.s100) => {
    fill(bg);
    pdf.roundedRect(x, y, w, h, h / 2, h / 2, 'F');
    const fillW = Math.min(w, Math.max(0, (w * clamp(pct)) / 100));
    if (fillW > 0) {
      fill(color);
      pdf.roundedRect(x, y, fillW, h, h / 2, h / 2, 'F');
      if (fillW < h) pdf.rect(x, y, fillW, h, 'F');
    }
  };

  const micro = (value, x, y, color = C.s400, size = 4.6) => {
    fnt(size, 'bold');
    txt(color);
    pdf.text(String(value || '').toUpperCase(), x, y);
  };

  const section = (title, subtitle, x, y, color = C.blue) => {
    fill(color);
    pdf.roundedRect(x, y + 0.8, 2.2, 8, 1, 1, 'F');
    micro(title, x + 5, y + 4.5, C.s400, 4.7);
    fnt(8.8, 'bold');
    txt(C.ink);
    pdf.text(String(subtitle || ''), x + 5, y + 10.5);
  };

  const scoreTone = (value) => {
    const score = number(value);
    if (score >= 85) return { label: 'Excelente', color: C.emerald, bg: [236, 253, 245], tx: [4, 120, 87] };
    if (score >= 70) return { label: 'Bueno', color: [132, 204, 22], bg: [247, 254, 231], tx: [63, 98, 18] };
    if (score >= 55) return { label: 'Aceptable', color: C.amber, bg: [255, 251, 235], tx: [146, 64, 14] };
    if (score >= 40) return { label: 'Alerta', color: C.orange, bg: [255, 247, 237], tx: [154, 52, 18] };
    return { label: 'Crítico', color: C.rose, bg: [255, 241, 242], tx: [190, 18, 60] };
  };

  const riskTone = (value) => {
    const score = number(value);
    if (score >= 65) return { label: 'Crítico', color: C.rose, bg: [255, 241, 242], tx: [190, 18, 60] };
    if (score >= 45) return { label: 'Alto', color: C.orange, bg: [255, 247, 237], tx: [154, 52, 18] };
    if (score >= 25) return { label: 'Medio', color: C.amber, bg: [255, 251, 235], tx: [146, 64, 14] };
    return { label: 'Bajo', color: C.emerald, bg: [236, 253, 245], tx: [4, 120, 87] };
  };

  const pill = (x, y, w, h, label, tone, size = 5.2) => {
    fill(tone.bg);
    pdf.roundedRect(x, y, w, h, h / 2, h / 2, 'F');
    fnt(size, 'bold');
    txt(tone.tx);
    pdf.text(String(label || ''), x + w / 2, y + h / 2 + size * 0.18, { align: 'center' });
  };

  const drawHeader = () => {
    fill(C.dark);
    pdf.rect(0, 0, W, HDR_H, 'F');
    fill(C.blue);
    pdf.rect(0, HDR_H - 1.6, W, 1.6, 'F');

    if (logoDataUrl) {
      const logoW = 19;
      const logoH = logoW * (47 / 106);
      pdf.addImage(logoDataUrl, 'PNG', ML, 4.1, logoW, logoH);
    } else {
      fnt(9, 'bold');
      txt(C.white);
      pdf.text('lytiks', ML, 10.5);
    }

    stroke([255, 255, 255]);
    pdf.setLineWidth(0.22);
    pdf.line(ML + 25, 4, ML + 25, 13);
    fnt(6.2, 'normal');
    txt([176, 196, 222]);
    pdf.text('Barómetro Naviero · Briefing Ejecutivo', ML + 30, 10.5);
    fnt(6.2, 'bold');
    txt(C.white);
    pdf.text(period, W - ML, 10.5, { align: 'right' });
  };

  const drawFooter = () => {
    line(ML, FOOTER_Y - 3, CW, C.s200, 0.18);
    fnt(5.2, 'normal');
    txt(C.s400);
    pdf.text('Confidencial · Barómetro Naviero · Lytiks Analytics', ML, FOOTER_Y + 2);
    pdf.text(`${page} / ${totalPages}`, W - ML, FOOTER_Y + 2, { align: 'right' });
  };

  const newPage = () => {
    pdf.addPage();
    page += 1;
    fill(C.white);
    pdf.rect(0, 0, W, H, 'F');
    drawHeader();
  };

  const drawMetricCell = (x, y, w, label, value, helper, color = C.blue) => {
    micro(label, x, y, C.s400, 4.3);
    fnt(10, 'bold');
    txt(color);
    pdf.text(wrap(value, w, 1)[0] || '—', x, y + 7);
    if (helper) {
      fnt(5.7, 'normal');
      txt(C.s500);
      pdf.text(wrap(helper, w, 1)[0], x, y + 13);
    }
  };

  const insightText = (item) => {
    if (typeof item === 'string') return item;
    const score = typeof item?.value === 'number' ? `${oneDecimal(item.value)} pts` : '';
    return item?.summary || `${item?.dimension || 'Dimensión'}: ${item?.question || item?.label || 'Señal'} ${score}`;
  };

  const drawInsightTable = (x, y, w, h) => {
    rect(x, y, w, h, C.white, C.s200, 2);
    section('Prioridades', 'Señales que requieren gestión', x + 5, y + 5, C.rose);
    const rows = [...(summaryInsights.critical || []), ...(summaryInsights.alerts || [])].slice(0, 4);
    let ry = y + 22;
    if (!rows.length) {
      fnt(6.4, 'normal');
      txt(C.s500);
      pdf.text('Sin brechas críticas en este período.', x + 10, ry);
      return;
    }
    rows.forEach((row, index) => {
      const isCritical = (row.severity || '').toLowerCase().includes('crítico') || number(row.value) < 40;
      const tone = isCritical ? scoreTone(20) : scoreTone(50);
      fill(tone.color);
      pdf.circle(x + 8, ry - 1.5, 1.1, 'F');
      fnt(5.8, 'bold');
      txt(tone.color);
      pdf.text(String(index + 1).padStart(2, '0'), x + 11, ry);
      fnt(6.1, 'normal');
      txt(C.s700);
      wrap(insightText(row), w - 45, 2).forEach((lineText, lineIndex) => {
        pdf.text(lineText, x + 19, ry + lineIndex * 4.4);
      });
      if (typeof row.value === 'number') {
        fnt(6.2, 'bold');
        txt(tone.color);
        pdf.text(oneDecimal(row.value), x + w - 7, ry, { align: 'right' });
      }
      ry += 10.5;
    });
  };

  const drawDimensionPanel = (x, y, w, h) => {
    rect(x, y, w, h, C.white, C.s200, 2);
    section('Índice', 'Pilares del barómetro', x + 5, y + 5, C.blue);
    let ry = y + 23;
    dimensionDrivers.slice(0, 5).forEach((driver, index) => {
      const color = DIM_COLORS[index % DIM_COLORS.length];
      const score = number(driver.dimensionScore);
      const label = DIM_LABEL[driver.name] || driver.name;
      const delta = dimensionDeltas[driver.name];
      fnt(6.3, 'bold');
      txt(C.s800);
      pdf.text(wrap(label, 39, 1)[0], x + 7, ry);
      bar(x + 48, ry - 3.1, w - 74, 3.2, score, color, C.s100);
      fnt(6.3, 'bold');
      txt(color);
      pdf.text(oneDecimal(score), x + w - 17, ry, { align: 'right' });
      if (delta != null) {
        fnt(5.2, 'bold');
        txt(delta >= 0 ? C.emerald : C.rose);
        pdf.text(`${delta >= 0 ? '+' : ''}${oneDecimal(delta)}`, x + w - 5, ry, { align: 'right' });
      }
      ry += 8.2;
    });
  };

  const drawCompactRows = ({ x, y, w, title, subtitle, rows, scoreKey = 'score', risk = false, empty = 'Sin datos suficientes.' }) => {
    rect(x, y, w, 58, C.white, C.s200, 2);
    section(title, subtitle, x + 5, y + 5, risk ? C.rose : C.emerald);
    let ry = y + 23;
    if (!rows.length) {
      fnt(6, 'normal');
      txt(C.s500);
      pdf.text(empty, x + 10, ry);
      return;
    }
    rows.slice(0, 5).forEach((item, index) => {
      const value = number(item[scoreKey]);
      const tone = risk ? riskTone(value) : scoreTone(value);
      fnt(6.3, 'bold');
      txt(C.ink);
      pdf.text(`${index + 1}. ${wrap(item.name || item.label, w - 43, 1)[0]}`, x + 8, ry);
      fnt(5.2, 'normal');
      txt(C.s500);
      const helper = item.topProblems?.[0]?.label || item.highlight || item.confidenceLabel || `${item.count || item.sampleSize || 0} registros`;
      pdf.text(wrap(helper, w - 43, 1)[0], x + 8, ry + 4.3);
      fnt(7.2, 'bold');
      txt(tone.color);
      pdf.text(oneDecimal(value), x + w - 7, ry, { align: 'right' });
      ry += 9.8;
    });
  };

  const drawShareList = (x, y, w, title, rows, color = C.blue, maxRows = 5) => {
    rect(x, y, w, 62, C.white, C.s200, 2);
    micro(title, x + 6, y + 8, C.s400, 4.6);
    let ry = y + 17;
    if (!rows.length) {
      fnt(6, 'normal');
      txt(C.s500);
      pdf.text('Sin datos.', x + 6, ry);
      return;
    }
    rows.slice(0, maxRows).forEach((row, index) => {
      const pct = number(row.percent);
      fnt(6.3, index === 0 ? 'bold' : 'normal');
      txt(C.s800);
      pdf.text(wrap(row.label, w - 50, 1)[0], x + 6, ry);
      bar(x + w - 41, ry - 3, 25, 2.7, pct, index === 0 ? color : C.s300, C.s100);
      fnt(6.1, 'bold');
      txt(index === 0 ? color : C.s500);
      pdf.text(`${pct}%`, x + w - 6, ry, { align: 'right' });
      ry += 8.5;
    });
  };

  const drawPageOne = () => {
    fill(C.white);
    pdf.rect(0, 0, W, H, 'F');
    drawHeader();
    let y = BODY_Y;

    fnt(15, 'bold');
    txt(C.ink);
    pdf.text('Resumen Ejecutivo', ML, y);
    fnt(7, 'normal');
    txt(C.s500);
    pdf.text('Lectura compacta del período y señales accionables', ML, y + 7);
    if (globalDeltaMeta) {
      fnt(7, 'bold');
      txt(globalDeltaMeta.formatted?.startsWith('+') ? C.emerald : C.rose);
      pdf.text(`${globalDeltaMeta.formatted} vs período anterior`, W - ML, y, { align: 'right' });
    }
    y += 15;

    rect(ML, y, CW, 34, C.white, C.s200, 2);
    fill(C.blue);
    pdf.rect(ML, y, 3, 34, 'F');
    fnt(24, 'bold');
    txt(C.blue);
    pdf.text(oneDecimal(globalIndex), ML + 10, y + 20);
    fnt(7.2, 'bold');
    txt(scoreTone(globalIndex).tx);
    pdf.text(interpretation?.text || scoreTone(globalIndex).label, ML + 10, y + 28);

    const cellY = y + 10;
    drawMetricCell(ML + 55, cellY, 30, 'Participantes', String(participantCount), 'empresas aprobadas', C.ink);
    drawMetricCell(ML + 91, cellY, 38, 'Foco crítico', laggingDimension?.name || 'Sin brechas', laggingDimension ? `${oneDecimal(laggingDimension.dimensionScore)} pts` : '', C.rose);
    drawMetricCell(ML + 137, cellY, 31, 'Mercado', trendStats[0]?.label || 'Por levantar', trendStats[0] ? `${trendStats[0].value} menc.` : '', C.amber);
    drawMetricCell(ML + 172, cellY, 20, 'Sent.', sentimentDisplay ? `${sentimentDisplay.negativePct}%` : '—', 'alerta', C.rose);
    y += 42;

    drawDimensionPanel(ML, y, 86, 62);
    drawInsightTable(ML + 92, y, CW - 92, 62);
    y += 70;

    if (executiveSummary) {
      rect(ML, y, CW, 22, [248, 251, 255], [210, 226, 248], 2);
      fill(C.blue);
      pdf.rect(ML, y, 2.4, 22, 'F');
      fnt(6.4, 'normal');
      txt(C.s700);
      wrap(executiveSummary, CW - 14, 3).forEach((lineText, index) => {
        pdf.text(lineText, ML + 8, y + 8 + index * 4.8);
      });
      y += 30;
    }

    const best = [...navieraStats].sort((a, b) => number(b.overallScore) - number(a.overallScore)).slice(0, 3);
    const risk = [...navieraStats].sort((a, b) => number(b.complaintRisk) - number(a.complaintRisk)).slice(0, 3);
    rect(ML, y, CW, 39, C.white, C.s200, 2);
    section('Navieras', 'Lectura rápida de desempeño y fricción', ML + 5, y + 5, C.blue);
    const half = (CW - 12) / 2;
    fnt(5.6, 'bold');
    txt(C.emerald);
    pdf.text('Mejor desempeño', ML + 8, y + 24);
    best.forEach((item, index) => {
      fnt(5.8, 'normal');
      txt(C.s700);
      pdf.text(`${index + 1}. ${wrap(item.name, half - 26, 1)[0]}`, ML + 8, y + 31 + index * 5);
      fnt(5.8, 'bold');
      txt(C.emerald);
      pdf.text(oneDecimal(item.overallScore), ML + half - 2, y + 31 + index * 5, { align: 'right' });
    });
    fnt(5.6, 'bold');
    txt(C.rose);
    pdf.text('Mayor fricción', ML + half + 14, y + 24);
    risk.forEach((item, index) => {
      fnt(5.8, 'normal');
      txt(C.s700);
      pdf.text(`${index + 1}. ${wrap(item.name, half - 26, 1)[0]}`, ML + half + 14, y + 31 + index * 5);
      fnt(5.8, 'bold');
      txt(C.rose);
      pdf.text(oneDecimal(item.complaintRisk), W - ML - 8, y + 31 + index * 5, { align: 'right' });
    });

    drawFooter();
  };

  const drawAiPage = () => {
    newPage();
    let y = BODY_Y;
    const ai = aiNarrative || {};

    fnt(14, 'bold');
    txt(C.ink);
    pdf.text('Narrativa Ejecutiva AI', ML, y);
    fnt(7, 'normal');
    txt(C.s500);
    pdf.text('Síntesis generada con Gemini desde datos agregados del período', ML, y + 7);
    y += 16;

    rect(ML, y, CW, 36, C.dark, C.dark, 2);
    micro('Lectura central', ML + 7, y + 9, [169, 194, 224], 4.7);
    fnt(12, 'bold');
    txt(C.white);
    wrap(ai.headline || 'Lectura ejecutiva del período', CW - 14, 2).forEach((lineText, index) => {
      pdf.text(lineText, ML + 7, y + 18 + index * 6);
    });
    fnt(6.4, 'normal');
    txt([196, 213, 233]);
    wrap(ai.overview || '', CW - 14, 2).forEach((lineText, index) => {
      pdf.text(lineText, ML + 7, y + 30 + index * 4.5);
    });
    y += 44;

    const half = (CW - 6) / 2;
    rect(ML, y, half, 42, C.white, C.s200, 2);
    section('Evolución', 'Cambio del período', ML + 5, y + 5, C.blue);
    fnt(6.2, 'normal');
    txt(C.s700);
    wrap(ai.evolutionInsight || 'Sin lectura de evolución disponible.', half - 14, 5).forEach((lineText, index) => {
      pdf.text(lineText, ML + 8, y + 24 + index * 4.4);
    });

    rect(ML + half + 6, y, half, 42, C.white, C.s200, 2);
    section('Narrativa', 'Lectura ejecutiva', ML + half + 11, y + 5, C.teal);
    fnt(6.2, 'normal');
    txt(C.s700);
    wrap(ai.executiveNarrative || '', half - 14, 5).forEach((lineText, index) => {
      pdf.text(lineText, ML + half + 14, y + 24 + index * 4.4);
    });
    y += 50;

    const criticalPillars = Array.isArray(ai.criticalPillars) ? ai.criticalPillars.slice(0, 4) : [];
    const navieraComplaints = Array.isArray(ai.navieraComplaints) ? ai.navieraComplaints.slice(0, 4) : [];
    drawCompactRows({
      x: ML,
      y,
      w: half,
      title: 'Pilares',
      subtitle: 'Críticos del levantamiento',
      rows: criticalPillars.map((item) => ({ ...item, label: item.name, score: item.score, highlight: item.reason })),
    });
    drawCompactRows({
      x: ML + half + 6,
      y,
      w: half,
      title: 'Navieras',
      subtitle: 'Mayor fricción',
      rows: navieraComplaints.map((item) => ({ ...item, label: item.name, risk: item.priority === 'Alta' ? 75 : item.priority === 'Media' ? 48 : 22, highlight: item.evidence })),
      scoreKey: 'risk',
      risk: true,
    });
    y += 66;

    const actions = Array.isArray(ai.recommendedActions) ? ai.recommendedActions.slice(0, 5) : [];
    rect(ML, y, CW, 52, C.white, C.s200, 2);
    section('Acciones', 'Recomendaciones ejecutivas', ML + 5, y + 5, C.blue);
    let ry = y + 24;
    actions.forEach((action, index) => {
      fill(C.blue);
      pdf.circle(ML + 8, ry - 1.5, 1, 'F');
      fnt(6.2, 'normal');
      txt(C.s700);
      pdf.text(wrap(action, CW - 20, 1)[0], ML + 12, ry);
      ry += 6.2;
    });

    drawFooter();
  };

  const drawPageTwo = () => {
    newPage();
    let y = BODY_Y;

    fnt(14, 'bold');
    txt(C.ink);
    pdf.text('Navieras, Segmentos e Incidentes', ML, y);
    fnt(7, 'normal');
    txt(C.s500);
    pdf.text('Cruces que convierten el cuestionario en decisiones operativas', ML, y + 7);
    y += 16;

    const half = (CW - 6) / 2;
    const best = [...navieraStats].sort((a, b) => number(b.overallScore) - number(a.overallScore));
    const risk = [...navieraStats].sort((a, b) => number(b.complaintRisk) - number(a.complaintRisk));
    drawCompactRows({ x: ML, y, w: half, title: 'Desempeño', subtitle: 'Ranking global por naviera', rows: best, scoreKey: 'overallScore' });
    drawCompactRows({ x: ML + half + 6, y, w: half, title: 'Fricción', subtitle: 'Riesgo de queja e impacto', rows: risk, scoreKey: 'complaintRisk', risk: true });
    y += 66;

    rect(ML, y, CW, 48, C.white, C.s200, 2);
    section('Segmentos', 'Dónde se concentra la presión', ML + 5, y + 5, C.blue);
    const segmentRows = [
      { label: 'Sector', item: segmentPressure?.sectors?.[0], color: C.blue },
      { label: 'Destino', item: segmentPressure?.destinations?.[0], color: C.teal },
      { label: 'Puerto', item: segmentPressure?.ports?.[0], color: C.amber },
    ].filter((row) => row.item);
    if (!segmentRows.length) {
      fnt(6.2, 'normal');
      txt(C.s500);
      pdf.text('Aún no hay suficientes datos para segmentar presión.', ML + 10, y + 27);
    }
    segmentRows.forEach((row, index) => {
      const x = ML + 8 + index * ((CW - 16) / 3);
      const w = (CW - 20) / 3;
      const item = row.item;
      const tone = scoreTone(item.score);
      micro(row.label, x, y + 25, C.s400, 4.3);
      fnt(7.3, 'bold');
      txt(C.ink);
      pdf.text(wrap(item.label, w - 17, 1)[0], x, y + 32);
      fnt(10, 'bold');
      txt(tone.color);
      pdf.text(oneDecimal(item.score), x + w, y + 32, { align: 'right' });
      bar(x, y + 36, w, 3, item.score, tone.color, C.s100);
      fnt(5.2, 'normal');
      txt(C.s500);
      const weak = item.weakestDimension?.name || item.weakestDimension || 'Sin brecha dominante';
      pdf.text(wrap(weak, w, 1)[0], x, y + 43);
    });
    y += 56;

    rect(ML, y, CW, 76, C.white, C.s200, 2);
    section('Incidentes', 'Severidad, resolución e impacto económico', ML + 5, y + 5, C.rose);
    const friction = operationalFriction || {};
    const stats = [
      { label: 'Incidentes aprox.', value: compactNumber(friction.incidentUnits), color: C.ink },
      { label: 'Alta severidad', value: `${friction.severeShare || 0}%`, color: C.orange },
      { label: 'Sin cierre total', value: `${friction.unresolvedShare || 0}%`, color: C.rose },
      { label: 'Evaluaciones', value: String(friction.evaluationCount || 0), color: C.blue },
    ];
    stats.forEach((stat, index) => {
      const x = ML + 8 + index * 43;
      micro(stat.label, x, y + 25, C.s400, 4.1);
      fnt(12, 'bold');
      txt(stat.color);
      pdf.text(stat.value, x, y + 35);
    });
    line(ML + 8, y + 42, CW - 16, C.s100);

    const problemRows = (friction.problemRows || []).slice(0, 4);
    const carrierRows = (friction.carrierRows || []).slice(0, 4);
    micro('Problemas dominantes', ML + 8, y + 51, C.s400, 4.4);
    problemRows.forEach((row, index) => {
      const ry = y + 59 + index * 5.8;
      fnt(5.8, 'normal');
      txt(C.s700);
      pdf.text(wrap(row.label, 55, 1)[0], ML + 8, ry);
      bar(ML + 67, ry - 2.4, 24, 2.4, row.percent || 0, C.rose, C.s100);
      fnt(5.8, 'bold');
      txt(C.rose);
      pdf.text(String(row.value || 0), ML + 96, ry, { align: 'right' });
    });
    micro('Navieras a monitorear', ML + 105, y + 51, C.s400, 4.4);
    carrierRows.forEach((row, index) => {
      const ry = y + 59 + index * 5.8;
      const tone = riskTone(row.risk);
      fnt(5.8, 'bold');
      txt(C.s700);
      pdf.text(wrap(row.name, 43, 1)[0], ML + 105, ry);
      fnt(5.8, 'bold');
      txt(tone.color);
      pdf.text(oneDecimal(row.risk), W - ML - 8, ry, { align: 'right' });
    });

    drawFooter();
  };

  const drawPageThree = () => {
    newPage();
    let y = BODY_Y;

    fnt(14, 'bold');
    txt(C.ink);
    pdf.text('Composición y Voz del Exportador', ML, y);
    fnt(7, 'normal');
    txt(C.s500);
    pdf.text('Base de participación y comentarios destacados', ML, y + 7);
    y += 16;

    const third = (CW - 10) / 3;
    drawShareList(ML, y, third, 'Sectores', sectorLeaderboard, C.blue, 5);
    drawShareList(ML + third + 5, y, third, 'Destinos', destinationLeaderboard, C.teal, 5);
    drawShareList(ML + (third + 5) * 2, y, third, 'Puertos', portLeaderboard, C.amber, 5);
    y += 72;

    rect(ML, y, CW, 40, C.white, C.s200, 2);
    section('Pilares', 'Mejor señal y brecha por dimensión', ML + 5, y + 5, C.indigo);
    const colW = (CW - 16) / Math.max(1, Math.min(5, dimensionDrivers.length || 1));
    dimensionDrivers.slice(0, 5).forEach((driver, index) => {
      const x = ML + 8 + index * colW;
      const color = DIM_COLORS[index % DIM_COLORS.length];
      const score = number(driver.dimensionScore);
      fnt(5.7, 'bold');
      txt(C.s700);
      pdf.text(wrap(DIM_LABEL[driver.name] || driver.name, colW - 4, 1)[0], x, y + 25);
      fnt(10, 'bold');
      txt(color);
      pdf.text(oneDecimal(score), x, y + 34);
      bar(x, y + 36, colW - 5, 2.8, score, color, C.s100);
    });
    y += 50;

    rect(ML, y, CW, 84, C.white, C.s200, 2);
    section('Verbatims', 'Voz del exportador', ML + 5, y + 5, C.blue);
    const tones = {
      positive: { label: 'Positivo', color: C.emerald, bg: [236, 253, 245], tx: [4, 120, 87] },
      neutral: { label: 'Neutral', color: C.s400, bg: C.s100, tx: C.s600 },
      negative: { label: 'Alerta', color: C.rose, bg: [255, 241, 242], tx: [190, 18, 60] },
    };
    let vy = y + 23;
    featuredVerbatims.slice(0, 4).forEach((item, index) => {
      const tone = tones[item.tone] || tones.neutral;
      fill(tone.color);
      pdf.roundedRect(ML + 7, vy - 2.8, 2, 12, 0.8, 0.8, 'F');
      fnt(6.3, 'italic');
      txt(C.s700);
      wrap(`"${item.quote}"`, CW - 48, 2).forEach((lineText, lineIndex) => {
        pdf.text(lineText, ML + 13, vy + lineIndex * 4.7);
      });
      fnt(5.5, 'bold');
      txt(C.s500);
      pdf.text(`Exportador ${String(index + 1).padStart(2, '0')} · ${item.naviera || 'Sin dato'}`, ML + 13, vy + 12);
      pill(W - ML - 28, vy + 1, 21, 7, tone.label, tone, 5);
      vy += 18;
    });

    drawFooter();
  };

  drawPageOne();
  if (aiNarrative) drawAiPage();
  drawPageTwo();
  drawPageThree();

  pdf.save(`barometro_${period}_ejecutivo.pdf`);
};
